import type { Timed, WorkerRequest, WorkerResponse } from "./protocol";
import type { TokensByLine, LangId, ThemeId } from "./types";

type Pending = {
  resolve: (v: unknown) => void;
  reject: (e: Error) => void;
};

export class HighlightWorker {
  private worker: Worker;
  private nextId = 1;
  private pending = new Map<number, Pending>();

  constructor(worker: Worker) {
    this.worker = worker;
    this.worker.addEventListener("message", (e: MessageEvent<WorkerResponse>) => {
      this.onMessage(e.data);
    });
    this.worker.addEventListener("error", (e: ErrorEvent) => {
      const err = new Error(`Highlight worker crashed: ${e.message}`);
      for (const p of this.pending.values()) p.reject(err);
      this.pending.clear();
    });
  }

  private onMessage(msg: WorkerResponse): void {
    const p = this.pending.get(msg.id);
    if (!p) return;
    this.pending.delete(msg.id);
    if (msg.ok) p.resolve(msg.value);
    else p.reject(new Error(msg.error));
  }

  private send<T>(make: (id: number) => WorkerRequest): Promise<T> {
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this.worker.postMessage(make(id));
    });
  }

  warmup(theme: ThemeId): Promise<void> {
    return this.send<null>((id) => ({ id, cmd: "warmup", theme })).then(() => undefined);
  }

  tokenize(
    text: string,
    lang: LangId,
    theme: ThemeId,
    signal?: AbortSignal,
  ): Promise<Timed<TokensByLine>> {
    const id = this.nextId++;
    const promise = new Promise<Timed<TokensByLine>>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this.worker.postMessage({ id, cmd: "tokenize", text, lang, theme } satisfies WorkerRequest);
    });
    if (signal) {
      if (signal.aborted) {
        this.cancel(id);
      } else {
        signal.addEventListener("abort", () => this.cancel(id), { once: true });
      }
    }
    return promise;
  }

  registerGrammar(grammar: unknown, extensions: string[]): Promise<Timed<null>> {
    return this.send<Timed<null>>((id) => ({ id, cmd: "registerGrammar", grammar, extensions }));
  }

  private cancel(targetId: number): void {
    const id = this.nextId++;
    // Cancel ack we don't await — fire and forget.
    this.worker.postMessage({ id, cmd: "cancel", targetId } satisfies WorkerRequest);
  }

  terminate(): void {
    this.worker.terminate();
    for (const p of this.pending.values()) p.reject(new Error("Highlight worker terminated"));
    this.pending.clear();
  }
}

export function createHighlightWorker(): HighlightWorker {
  const worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
  return new HighlightWorker(worker);
}

